import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, Wallet, ClockCounterClockwise, ArrowClockwise,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getGroupCode,
  getTickets,
  getSubscriberDetails,
  insertSubscriberIncome,
  saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function PsIncomeNotSaveForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [, setChitgroupId] = useState(null);
  const [subscriberId, setSubscriberId] = useState(null);
  const [branchId, setBranchId] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBranchCode, setPendingBranchCode] = useState(null);
  const [pendingGroupCode, setPendingGroupCode] = useState(null);
  const [pendingTicketNo, setPendingTicketNo] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  // Ticket changed -> only subscriberId resets (branch_id NOT touched)
  const resetFromTicket = () => {
    setSubscriberId(null);
  };

  // Group changed -> reset group-level + everything downstream (branch_id NOT touched)
  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  // Branch changed -> reset branch-level + everything downstream
  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setBranchId(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchlist.find((x) => x.branch_name === branchName);
    if (!selected) return;

    setBranchschema(selected.branch_code);
    setBranchCode(selected.branch_code);
    setBranchId(selected.tbl_mst_branch_configuration_id); // captured here, kept until branch changes again

    try {
      const res = await getGroupCode(selected.branch_code);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = branchCodeList.find((x) => x.group_Code === groupCodeValue);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleTicketChange = async (ticketno) => {
    setTicket(ticketno);
    resetFromTicket();
    if (!ticketno) return;

    try {
      const res = await getSubscriberDetails(branchschema, groupCode, ticketno);
      if (!res || res.length === 0) {
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
        return;
      }
      setSubscriberId(res[0].tbl_mst_subscriber_id);
    } catch {
      setSubscriberId(null);
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && !!ticket
    && reason.trim().length >= 10 && reason.length <= 200;

  /**
   * Validates the form and opens the confirm modal.
   * Actual save happens in doSave() once the user confirms.
   */
  const confirmSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!subscriberId || !branchId) {
      showToast('warn', 'Subscriber Not Loaded', 'Subscriber details not loaded. Please re-select the ticket.');
      return;
    }

    setPendingBranchCode(branchCode);
    setPendingGroupCode(group);
    setPendingTicketNo(ticket);
    setPendingReason(reason);

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
  };

  /**
   * Runs the actual save once the user confirms in the modal.
   * Same two-step flow as before: insert income, then save audit log.
   */
  const doSave = async () => {
    setIsSaving(true);

    try {
      // Step 1: Insert subscriber income first
      const result = await insertSubscriberIncome(branchschema, subscriberId, branchId);

      // adjust this check based on what your API actually returns
      // e.g. result === true, result.success === true, result.status === 'OK', etc.
      if (!result) {
        setIsSaving(false);
        setShowConfirmModal(false);
        showToast('error', 'Insert Failed', 'Insert failed. Audit not saved.', true);
        return;
      }

      // Step 2: Build oldData/newData, then audit (matches Groupcodechange pattern)
      const oldData = {
        branchCode,
        groupCode: group,
        ticketNo: ticket,
      };

      const newData = {
        branchCode,
        groupCode: group,
        ticketNo: ticket,
        subscriberId,
        branch_id: branchId,
      };

      const audit = {
        schemaName: branchschema,
        loginName,
        loginTime: new Date().toISOString(),
        changeType: 'PS_SUBSCRIBER_INCOME_CHANGE',
        oldData,
        newData,
        reason,
      };

      try {
        await saveData([audit]);
        setIsSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Subscriber income inserted and audit saved successfully.', true);
        handleClear();
      } catch (err) {
        setIsSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', err);
        showToast('warn', 'Partial Success', 'Income inserted, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setIsSaving(false);
      setShowConfirmModal(false);
      console.error('InsertSubscriberIncome failed:', err);
      showToast('error', 'Insert Failed', 'Could not insert subscriber income. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="ps-income-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="ps-income-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Wallet size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Subscriber Income</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>PS Income Not Save</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Subscriber */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Subscriber</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket number to load subscriber details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="ps-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="ps-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No. <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="ps-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!cao || !group || !ticket} hint="Select branch, group code and ticket in section A first">
        {/* SECTION B: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="ps-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : reason.trim().length < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="ps-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={confirmSave} className="btn-primary" disabled={isSaving} data-testid="ps-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="ps-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Wallet size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Subscriber Income</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to insert subscriber income for this ticket. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Branch / Group</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingBranchCode} / {pendingGroupCode}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Ticket No</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingTicketNo}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="ps-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={isSaving} className="btn-primary" data-testid="ps-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PsIncomeNotSaveForm;